import {useEffect, useRef, useState} from "react";
import {convertFromFinal, PipelineElement} from "../../pipeline-builder/PipelineConfig";
import {agent} from "../../../api/agent";
import {Skeleton} from "../../../api/types";
import {findElement} from "../../utils/PipelineHelper";
import styled from "styled-components";
import PipelineTree from "../../pipeline-builder/PipelineTree";
import LoadingContainer from "../../LoadingContainer";


interface Props {
    open: boolean;
    skeleton: Skeleton;
    followedWorker?: string | null;
}

const LogsContainer = styled.div`
    overflow-y: scroll;
    width: 100%;
    transition: height 0.3s ease-in-out;
`;

const LOG_EVENTS_PER_FRAME = 200;

export default function JobLogs({skeleton, open, followedWorker}: Props) {

    const [ci, setCi] = useState<PipelineElement | null>(null);
    const ciRef = useRef<PipelineElement | null>(null);
    const logsContainerRefs = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        convertFromFinal(JSON.parse(skeleton.ci)).then(elements => {
            if (!cancelled) {
                ciRef.current = elements[0];
                setCi(elements[0]);
                setLoading(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [skeleton.ci]);

    useEffect(() => {

        if (!open || loading) {
            return;
        }

        let buffer: any[] = [];
        let animationFrame: number | null = null;
        const processBuffer = () => {
            // Replaying a large historical log can produce thousands of events. Processing a
            // bounded batch per frame keeps navigation and painting responsive while it loads.
            const batch = buffer.splice(0, LOG_EVENTS_PER_FRAME);
            const logSkeleton = ciRef.current;
            if (!logSkeleton) {
                animationFrame = null;
                return;
            }

            for (const data of batch) {
                switch (data.type) {
                    case 'initial': {
                        if (data.from && data.from === 'skeleton') {
                            logSkeleton.id = data.id;
                            console.log('Skeleton initialized with id:', data.id);
                        }
                        let element = findElement(data.id, logSkeleton);
                        if (!element) {
                            console.error('Element not found for id:', data.id);
                            break;
                        }
                        element.lastStatus = data.status;
                        element.logs = data.logs;
                        break;
                    }
                    case 'status': {
                        let element = findElement(data.pipelineId, logSkeleton);
                        if (!element) {
                            console.error('Element not found for id:', data.pipelineId);
                            break;
                        }
                        element.lastStatus = data.status;
                        break;
                    }
                    case 'log': {
                        let element = findElement(data.pipelineId, logSkeleton);
                        if (!element) {
                            console.error('Element not found for id:', data.pipelineId);
                            break;
                        }
                        if (!element.logs) {
                            element.logs = [];
                        }
                        element.logs.push({
                            level: data.level,
                            message: data.message
                        });
                        break;
                    }
                }
            }
            if (batch.length > 0) {
                // Descendants are already updated in place. A new root object triggers React
                // without serialising and copying the entire accumulated log tree.
                const nextSkeleton = {...logSkeleton};
                ciRef.current = nextSkeleton;
                setCi(nextSkeleton);
            }

            if (buffer.length > 0) {
                animationFrame = requestAnimationFrame(processBuffer);
            } else {
                animationFrame = null;
            }
        };
        const callback = (data: any) => {
            buffer.push(data);
            if (animationFrame === null) {
                animationFrame = requestAnimationFrame(processBuffer);
            }
        }

        const id = agent.subscribe('/skeleton/logs/' + skeleton.id, callback);

        return () => {
            agent.unsubscribe(id);
            if (animationFrame !== null) {
                cancelAnimationFrame(animationFrame);
            }
            buffer = [];
        };
    }, [open, loading, skeleton.id]);

    return <LoadingContainer open={loading && open}>

        <LogsContainer style={{height: open ? '31rem' : '0'}}
                       className={'logsarea'}
                       ref={logsContainerRefs}>
            {ci && (
                <PipelineTree current={ci} active={null} isNotTest={true}
                              htmlElement={logsContainerRefs!.current}
                              isRunning={ci.lastStatus === 'running'}
                              followedWorker={followedWorker}></PipelineTree>)}
        </LogsContainer>
    </LoadingContainer>;

}
